import { useState } from "react";
import { cn } from "@/lib/cn";
import type { AuthUser } from "@billing/shared";

// Dyuthi Accounts sessions carry the user's picture URL (`picture`, or null for
// none). Keycloak sessions have no `picture` key: the SSO portal serves their
// avatar keyed by the Keycloak user id (`sub`). Initials on load error.
const AVATAR_BASE = "https://sso.dyuthitech.in/portal/avatars";

export function avatarUrlFor(
  user: Pick<AuthUser, "sub" | "picture"> | null | undefined,
): string | null {
  if (!user) return null;
  if (user.picture !== undefined) return user.picture?.startsWith("https://") ? user.picture : null;
  return user.sub ? `${AVATAR_BASE}/${user.sub}.png` : null;
}

export function initialFor(
  user: Pick<AuthUser, "name" | "email"> | null | undefined,
): string {
  return (user?.name || user?.email || "U").trim()[0]?.toUpperCase() ?? "U";
}

export function Avatar({
  user,
  size = 32,
  className,
}: {
  user: Pick<AuthUser, "sub" | "picture" | "name" | "email"> | null | undefined;
  size?: number;
  className?: string;
}) {
  const url = avatarUrlFor(user);
  const [failed, setFailed] = useState(false);
  const initial = initialFor(user);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={user?.name ?? "Profile"}
        width={size}
        height={size}
        onError={() => setFailed(true)}
        className={cn("shrink-0 rounded-full object-cover", className)}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-bold",
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initial}
    </span>
  );
}
